// Structured jsPDF dossier generator.
// We deliberately do NOT use html2canvas because the app stylesheet uses
// oklch() color tokens which html2canvas v1 cannot parse — it throws and
// the PDF download silently fails. Generating the PDF from structured
// data is also far more legible than rasterising a dark glassmorphism UI.

import type { Case, Evidence, Note, CustodyEvent } from "./store";
import type { BackendScanResult } from "./scan.functions";

type Doc = import("jspdf").jsPDF;

const MARGIN = 14;
const LINE = 5;

function ensureFilename(name: string) {
  return name.endsWith(".pdf") ? name : `${name}.pdf`;
}

function fmtTs(iso: string) {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

async function loadJsPDF() {
  const { jsPDF } = await import("jspdf");
  return new jsPDF("p", "mm", "a4");
}

class Cursor {
  y = MARGIN;
  constructor(public doc: Doc) {}
  pageH() { return this.doc.internal.pageSize.getHeight(); }
  pageW() { return this.doc.internal.pageSize.getWidth(); }
  ensure(h = LINE) {
    if (this.y + h > this.pageH() - MARGIN) {
      this.doc.addPage();
      this.y = MARGIN;
    }
  }
  text(s: string, opts: { size?: number; bold?: boolean; color?: [number, number, number]; gap?: number } = {}) {
    const { size = 10, bold = false, color = [20, 20, 20], gap = 1 } = opts;
    this.doc.setFont("helvetica", bold ? "bold" : "normal");
    this.doc.setFontSize(size);
    this.doc.setTextColor(color[0], color[1], color[2]);
    const maxW = this.pageW() - MARGIN * 2;
    const lines = this.doc.splitTextToSize(s ?? "", maxW) as string[];
    for (const ln of lines) {
      this.ensure(size * 0.45 + 1);
      this.doc.text(ln, MARGIN, this.y);
      this.y += size * 0.45 + 1;
    }
    this.y += gap;
  }
  rule() {
    this.ensure(3);
    this.doc.setDrawColor(180, 180, 180);
    this.doc.line(MARGIN, this.y, this.pageW() - MARGIN, this.y);
    this.y += 3;
  }
  section(title: string) {
    this.y += 2;
    this.text(title.toUpperCase(), { size: 11, bold: true, color: [10, 90, 140], gap: 1 });
    this.rule();
  }
  kv(k: string, v: string) {
    this.text(`${k}: ${v}`, { size: 9, color: [60, 60, 60] });
  }
}

function header(c: Cursor, title: string, subtitle: string) {
  c.doc.setFillColor(8, 17, 31);
  c.doc.rect(0, 0, c.pageW(), 22, "F");
  c.doc.setTextColor(0, 220, 255);
  c.doc.setFont("helvetica", "bold");
  c.doc.setFontSize(14);
  c.doc.text("CARIO INTEL · INVESTIGATION REPORT", MARGIN, 10);
  c.doc.setFont("helvetica", "normal");
  c.doc.setFontSize(9);
  c.doc.setTextColor(200, 220, 230);
  c.doc.text(subtitle, MARGIN, 16);
  c.y = 28;
  c.text(title, { size: 16, bold: true, color: [15, 30, 50], gap: 2 });
}

function footer(doc: Doc) {
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    const w = doc.internal.pageSize.getWidth();
    const h = doc.internal.pageSize.getHeight();
    doc.text(`CARIO INTEL · Confidential · Generated ${new Date().toLocaleString()}`, MARGIN, h - 6);
    doc.text(`Page ${i} / ${pages}`, w - MARGIN, h - 6, { align: "right" });
  }
}

function emptyLine(c: Cursor) {
  c.text("(none)", { size: 9, color: [140, 140, 140], gap: 1 });
}

async function sha256Hex(input: string): Promise<string> {
  try {
    const buf = new TextEncoder().encode(input);
    const digest = await crypto.subtle.digest("SHA-256", buf);
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return "unavailable";
  }
}

export async function exportCaseDossier(args: {
  caseRow: Case;
  evidence: Evidence[];
  notes: Note[];
  custody: CustodyEvent[];
  chain: { valid: boolean; brokenAt?: string };
  filename?: string;
}): Promise<boolean> {
  if (typeof window === "undefined") return false;
  try {
    const doc = await loadJsPDF();
    const c = new Cursor(doc);
    const { caseRow, evidence, notes, custody, chain } = args;

    header(c, caseRow.title || "Untitled investigation",
      `${caseRow.id} · ${caseRow.classification} · target ${caseRow.target}`);

    c.section("Case overview");
    c.kv("Case ID", caseRow.id);
    c.kv("Target", caseRow.target);
    c.kv("Status", caseRow.status.toUpperCase());
    c.kv("Classification", caseRow.classification);
    c.kv("Owner", caseRow.owner);
    c.kv("Opened", fmtTs(caseRow.createdAt));
    c.kv("Last updated", fmtTs(caseRow.updatedAt));
    c.kv("Tags", caseRow.tags.length ? caseRow.tags.map((t) => `#${t}`).join(" ") : "(none)");
    c.kv("Chain of custody", chain.valid ? "VERIFIED" : `BROKEN at ${chain.brokenAt ?? "unknown"}`);

    if (caseRow.summary) {
      c.section("Executive summary");
      c.text(caseRow.summary, { size: 10 });
    }

    c.section(`Evidence ledger (${evidence.length})`);
    if (!evidence.length) emptyLine(c);
    evidence.forEach((e, i) => {
      c.ensure(14);
      c.text(`${i + 1}. [${e.kind.toUpperCase()}] ${e.label}`, { size: 10, bold: true });
      c.kv("ID", e.id);
      c.kv("Source", e.source);
      c.kv("Hash", e.hash);
      c.kv("Collected", `${fmtTs(e.collectedAt)} by ${e.collectedBy}`);
      if (e.tags.length) c.kv("Tags", e.tags.map((t) => `#${t}`).join(" "));
      c.text(e.content, { size: 9, color: [40, 40, 40], gap: 2 });
    });

    c.section(`Analyst notes (${notes.length})`);
    if (!notes.length) emptyLine(c);
    notes.forEach((n, i) => {
      c.text(`${i + 1}. ${n.pinned ? "[PINNED] " : ""}${fmtTs(n.ts)} — ${n.author}`,
        { size: 9, bold: true, color: [80, 80, 80] });
      c.text(n.body, { size: 10, gap: 2 });
    });

    c.section(`Chain of custody (${custody.length} events)`);
    if (!custody.length) emptyLine(c);
    custody.forEach((ev) => {
      c.text(`${fmtTs(ev.ts)} · ${ev.action.toUpperCase()} · ${ev.actor}`,
        { size: 9, bold: true });
      const detail = [ev.ref ? `ref=${ev.ref}` : null, ev.detail].filter(Boolean).join(" · ");
      if (detail) c.text(detail, { size: 9, color: [70, 70, 70] });
      c.text(`hash ${ev.hash}`, { size: 8, color: [120, 120, 120], gap: 1 });
    });

    footer(doc);
    doc.save(ensureFilename(args.filename ?? `${caseRow.id}-dossier.pdf`));
    return true;
  } catch (err) {
    console.error("[pdf] case dossier export failed", err);
    return false;
  }
}

export async function exportScanDossier(args: {
  result: BackendScanResult;
  filename?: string;
}): Promise<boolean> {
  if (typeof window === "undefined") return false;
  try {
    const doc = await loadJsPDF();
    const c = new Cursor(doc);
    const { result } = args;

    header(c, `OSINT scan · ${result.target}`,
      `run ${result.id} · ${result.targetType.toUpperCase()} · ${result.sourceCount} collectors`);

    c.section("Scan summary");
    c.kv("Target", result.target);
    c.kv("Detected type", result.targetType);
    c.kv("Status", result.status.toUpperCase());
    c.kv("Risk score", `${result.riskScore} / 100`);
    c.kv("Confidence", `${result.confidence}%`);
    c.kv("Collectors executed", String(result.sourceCount));
    c.kv("Started", fmtTs(result.createdAt));
    c.kv("Completed", fmtTs(result.completedAt));
    c.kv("Legal scope", result.raw.legalScope);
    const integrity = await sha256Hex(JSON.stringify({
      id: result.id, target: result.target, riskScore: result.riskScore,
      confidence: result.confidence, findings: result.findings, entities: result.entities,
      timeline: result.timeline, completedAt: result.completedAt,
    }));
    c.kv("Report integrity (SHA-256)", integrity);

    c.section(`Findings (${result.findings.length})`);
    if (!result.findings.length) emptyLine(c);
    result.findings.forEach((f, i) => {
      c.ensure(12);
      c.text(`${i + 1}. [${f.severity.toUpperCase()}] ${f.title}`,
        { size: 10, bold: true });
      c.kv("Source", f.source);
      c.kv("Category", f.category);
      c.kv("Confidence", `${f.confidence}%`);
      c.kv("Timestamp", f.ts);
      c.text(f.description, { size: 9, color: [40, 40, 40], gap: 2 });
    });

    c.section(`Entities (${result.entities.length})`);
    if (!result.entities.length) emptyLine(c);
    result.entities.forEach((e, i) => {
      c.text(`${i + 1}. [${e.type.toUpperCase()}] ${e.label}${e.sub ? " — " + e.sub : ""}`,
        { size: 9 });
      c.kv("Confidence", `${e.confidence}%`);
      c.kv("Risk", `${e.risk}/100`);
    });

    const chrono = [...(result.timeline ?? [])].sort((a, b) => String(a.ts).localeCompare(String(b.ts)));
    const evidenceRows = chrono.filter((t) => (t as any).kind !== "run" && (t as any).category !== "run");
    const runRows = chrono.filter((t) => (t as any).kind === "run" || (t as any).category === "run");

    c.section(`Reconstructed timeline (${evidenceRows.length})`);
    if (!evidenceRows.length) emptyLine(c);
    evidenceRows.forEach((t) => {
      const cat = (t as any).category ? String((t as any).category).toUpperCase() : "EVIDENCE";
      const prec = (t as any).precision && (t as any).precision !== "day" ? ` (±${(t as any).precision})` : "";
      c.text(`${String(t.ts).slice(0, 10)}${prec} · [${cat}] ${t.title}`, { size: 9 });
      c.kv("Source", t.source);
      if ((t as any).ref) c.kv("Reference", String((t as any).ref));
    });

    if (runRows.length) {
      c.section(`Run activity (${runRows.length})`);
      runRows.forEach((t) => {
        c.text(`${t.ts} · ${t.title}`, { size: 9 });
        c.kv("Source", t.source);
      });
    }

    const collectors = Array.isArray(result.raw?.collectors) ? result.raw.collectors : [];
    c.section(`Collector telemetry (${collectors.length})`);
    if (!collectors.length) emptyLine(c);
    collectors.forEach((col) => {
      c.text(`${col.source} — ${col.status.toUpperCase()} (${col.latencyMs}ms)`,
        { size: 9, bold: true });
      c.text(col.summary, { size: 9, color: [70, 70, 70], gap: 1 });
    });

    footer(doc);
    doc.save(ensureFilename(args.filename ?? `${result.id}-scan.pdf`));
    return true;
  } catch (err) {
    console.error("[pdf] scan dossier export failed", err);
    return false;
  }
}

// Back-compat shim: legacy callers pass an element id. We can no longer
// rasterise oklch-styled DOM, so we no-op gracefully and return false so
// callers can show an error or fall back. Prefer exportCaseDossier /
// exportScanDossier.
export async function exportElementToPdf(_elementId: string, _filename: string): Promise<boolean> {
  console.warn("[pdf] exportElementToPdf is deprecated — use exportCaseDossier / exportScanDossier");
  return false;
}